import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './SliidingButton.module.css';

interface SlidingButtonOption {
  value: string;
  label?: string;
}

interface Props {
  options: SlidingButtonOption[];
  selected: string;
  setSelected: (value: string) => void;
  className?: string;
  inline?: boolean;
  fontSize?: string;
}

function SlidingButton({ options, selected, setSelected, className = '', inline = false, fontSize = '14px' }: Props) {
  const optionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [dimensions, setDimensions] = useState({
    width: 0,
    left: 0,
    height: 0,
  });

  const selectedOption = options.find((v) => v.value === selected);

  const updateSize = useCallback(
    (option = selected) => {
      const currentRef = optionRefs.current[option];
      if (currentRef) {
        setDimensions({
          width: currentRef.clientWidth || 0,
          left: currentRef.offsetLeft || 0,
          height: currentRef.clientHeight || 0,
        });
      }
    },
    [selected]
  );

  useEffect(() => {
    const handleResize = () => updateSize();
    window.addEventListener('resize', handleResize);
    updateSize();
    return () => window.removeEventListener('resize', handleResize);
  }, [updateSize]);

  const rootClassName = `${styles.sliderButtonRoot} ${inline ? styles.inlineWrapper : ''} ${className}`.trim();

  return (
    <div className={rootClassName}>
      <div
        className={styles.sliderFloating}
        style={{
          width: Math.max(0, dimensions.width - 4),
          left: dimensions.left + 2,
          height: 'calc(100% - 8px)',
        }}
      />
      {options.map((option) => {
        const isSelected = selectedOption?.value === option.value;
        return (
          <div
            key={option.value}
            role="button"
            tabIndex={0}
            onClick={() => {
              setSelected(option.value);
              updateSize(option.value);
            }}
            ref={(r) => {
              optionRefs.current[option.value] = r;
            }}
            className={`${styles.sliderButtonTab} ${isSelected ? styles.selected : ''} ${
              styles[`fontSize${fontSize.replace('px', '')}`]
            }`}
          >
            {option.label || option.value}
          </div>
        );
      })}
    </div>
  );
}

export default SlidingButton;
